import React, { Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { queryClient } from './providers/query-client';
import SuspenseFallback from './components/shared/SuspenseFallback';
import ProtectedRoute from './components/shared/ProtectedRoute';
import TokenRefreshHandler from './components/auth/TokenRefreshHandler';
import { isLoggedIn } from './app/auth';
import { ROUTE_PAGES } from './appsConfig';
import lazyWithRetry from './utils/lazyWithRetry';
import LazyChunkErrorBoundary from './components/shared/LazyChunkErrorBoundary';

function RootRedirect() {
  return isLoggedIn() ? <Navigate to="/launcher" replace /> : <Navigate to="/login" replace />;
}

const LoginPage = lazyWithRetry(ROUTE_PAGES.LoginPage);
const AppsPage = lazyWithRetry(() => import('./pages/shared/AppsPage'));
const AppLayout = lazyWithRetry(() => import('./layouts/AppLayout'));
const NotFoundPage = lazyWithRetry(ROUTE_PAGES.NotFoundPage);
const LegacyDashboardRedirectPage = lazyWithRetry(ROUTE_PAGES.LegacyDashboardRedirectPage);
const ForcePasswordChangePage = lazyWithRetry(ROUTE_PAGES.ForcePasswordChangePage);
const VerifyOtpPage = lazyWithRetry(ROUTE_PAGES.VerifyOtpPage);
const UnauthorizedPage = lazyWithRetry(ROUTE_PAGES.UnauthorizedPage);
const EventInvitationResponsePage = lazyWithRetry(() => import('./pages/shared/EventInvitationResponsePage'));
const DocsignSigningPage = lazyWithRetry(() => import('./pages/docsign/DocsignSigningPage'));

function FinanceRedirect() {
  const { pathname } = useLocation();
  const rest = pathname.replace(/^\/finance\/?/, '') || '';
  const to = rest ? `/app/finance/${rest}` : '/app/finance';
  return <Navigate to={to} replace />;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <LazyChunkErrorBoundary>
          <Suspense fallback={<SuspenseFallback />}>
            <TokenRefreshHandler />
            <div className="animate-fade-in">
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/launcher" element={<ProtectedRoute><AppsPage /></ProtectedRoute>} />
                <Route path="/apps" element={<Navigate to="/launcher" replace />} />
                <Route path="/app/:appId/*" element={<ProtectedRoute><AppLayout /></ProtectedRoute>} />
                <Route path="/dashboard/*" element={<LegacyDashboardRedirectPage />} />
                <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
                <Route path="/event-invite" element={<EventInvitationResponsePage />} />
                <Route path="/verify-otp" element={<VerifyOtpPage />} />
                <Route path="/docsign/sign/:token" element={<DocsignSigningPage />} />

                {/* Finance: redirect to app layout */}
                <Route path="/finance/*" element={<FinanceRedirect />} />

                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/forbidden" element={<UnauthorizedPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </Suspense>
        </LazyChunkErrorBoundary>
      </HashRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
